import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingType } from "../../../types/CommonTypes";
import { getPharmacyLicensesByPharmacyId } from "../../../service/PharmacyLicenseService";
import { useAppSelector } from "../redux-hooks";
import type { PharmacyLicense } from "../../../utils/rxOrderGating";
import { hydratePharmacyLicenseTable } from "../../../utils/rxOrderGating";

type PharmacyLicenseState = {
  pharmacyId: number | null;
  pharmacyName: string | null;
  pharmacyLicenses: PharmacyLicense[];
  fetchPharmacyLicensesStatus: LoadingType;
};

export type FetchPharmacyLicensesArgs = {
  /** Product manufacturer id — used as pharmacy_licenses.manufacturer_id / Gate 1 pharmacyId. */
  manufacturerId: number | null | undefined;
  manufacturerName?: string | null;
};

const initialState: PharmacyLicenseState = {
  pharmacyId: null,
  pharmacyName: null,
  pharmacyLicenses: [],
  fetchPharmacyLicensesStatus: "idle",
};

/**
 * CS-2073 — Load pharmacy_licenses for Gate 1 using the product's manufacturer id
 * (API param still named pharmacyId for compatibility).
 */
export const fetchPharmacyLicenses = createAsyncThunk(
  "fetchPharmacyLicenses",
  async ({ manufacturerId, manufacturerName }: FetchPharmacyLicensesArgs) => {
    if (!manufacturerId || manufacturerId <= 0) {
      hydratePharmacyLicenseTable([]);
      return {
        pharmacyId: null as number | null,
        pharmacyName: null as string | null,
        rows: [] as PharmacyLicense[],
      };
    }

    const rows = await getPharmacyLicensesByPharmacyId(manufacturerId);
    hydratePharmacyLicenseTable(rows);
    return {
      pharmacyId: manufacturerId,
      pharmacyName: manufacturerName ?? rows[0]?.pharmacy_name ?? null,
      rows,
    };
  }
);

const pharmacyLicenseSlice = createSlice({
  name: "pharmacyLicense",
  initialState,
  reducers: {
    resetPharmacyLicenseSlice: () => initialState,
    setPharmacyLicenses: (
      state,
      action: PayloadAction<{
        pharmacyId: number | null;
        pharmacyName: string | null;
        rows: PharmacyLicense[];
      }>
    ) => {
      state.pharmacyId = action.payload.pharmacyId;
      state.pharmacyName = action.payload.pharmacyName;
      state.pharmacyLicenses = action.payload.rows;
      hydratePharmacyLicenseTable(action.payload.rows);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPharmacyLicenses.pending, (state) => {
        state.fetchPharmacyLicensesStatus = "loading";
      })
      .addCase(fetchPharmacyLicenses.fulfilled, (state, action) => {
        state.fetchPharmacyLicensesStatus = "success";
        state.pharmacyId = action.payload.pharmacyId;
        state.pharmacyName = action.payload.pharmacyName;
        state.pharmacyLicenses = action.payload.rows;
      })
      .addCase(fetchPharmacyLicenses.rejected, (state) => {
        state.fetchPharmacyLicensesStatus = "error";
      });
  },
});

export const { resetPharmacyLicenseSlice, setPharmacyLicenses } =
  pharmacyLicenseSlice.actions;
export const usePharmacyLicenseReducer = () =>
  useAppSelector((state) => state.pharmacyLicenseSlice);
export default pharmacyLicenseSlice.reducer;
